import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, ChevronDown, Check } from "lucide-react";

/**
 * Replaces react-phone-number-input's default country picker -- a
 * native, invisible <select> layered over the flag icon, which opens the
 * browser/OS's own unstyled popup list -- with a custom searchable
 * dropdown that matches the site's design (rounded, shadowed, royal
 * accent). Pure UI swap: same `countrySelectComponent` contract
 * (value/onChange/options/iconComponent/...), so phone-number parsing,
 * validation, and the value passed to onChange are all untouched -- see
 * PhoneField.jsx, the single place this plugs in for every form on the
 * site.
 *
 * The panel is rendered through a portal into document.body, positioned
 * with `position: fixed` from the trigger's own bounding rect, instead of
 * as a normal absolutely-positioned child. The trigger lives inside
 * .zt-phone-input, which clips overflow to keep its own corners rounded,
 * and several call sites (e.g. BookingModal) also nest it inside a
 * scrollable, rounded modal body -- a plain absolute child would get
 * clipped or scroll away with the field instead of floating above it.
 */
export default function CountrySelect({
  value,
  onChange,
  options,
  disabled,
  readOnly,
  iconComponent: Icon,
  name,
  "aria-label": ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const listboxId = useId();

  const selected = useMemo(
    () => options.find((o) => (o.value || undefined) === (value || undefined)),
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const updateRect = () => {
    if (!triggerRef.current) return;
    setRect(triggerRef.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!open) return undefined;
    updateRect();
    setQuery("");
    setActiveIndex(0);
    const t = setTimeout(() => searchRef.current?.focus(), 0);

    const onDocMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (optValue) => {
    onChange(optValue || undefined);
    setOpen(false);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) pick(opt.value);
    }
  };

  const panelStyle = useMemo(() => {
    if (!rect) return { display: "none" };
    const width = 260;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : width;
    const left = Math.min(rect.left, viewportWidth - width - 8);
    return {
      position: "fixed",
      top: rect.bottom + 6,
      left: Math.max(8, left),
      width,
    };
  }, [rect]);

  return (
    // Keeping the "PhoneInputCountry" class (the library's own wrapper
    // class, previously rendered by its default CountrySelectWithIcon)
    // means every existing index.css rule scoped to it -- padding,
    // border-right, hover background, the dark-surface variant, the
    // course-detail-page tweaks -- keeps applying untouched; only the
    // dropdown itself (zt-country-*) is new.
    <div className="PhoneInputCountry zt-country-select" ref={triggerRef}>
      <button
        type="button"
        name={name}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled || readOnly}
        onClick={() => setOpen((o) => !o)}
        className="zt-country-trigger"
      >
        <Icon aria-hidden country={value} label={selected?.label} />
        <ChevronDown aria-hidden size={13} className="zt-country-trigger-arrow" />
      </button>

      {open &&
        createPortal(
          <div ref={panelRef} className="zt-country-popover" style={panelStyle}>
            <div className="zt-country-search">
              <Search aria-hidden size={14} />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={onSearchKeyDown}
                placeholder="Search country"
                aria-controls={listboxId}
              />
            </div>
            <div id={listboxId} role="listbox" aria-label={ariaLabel} className="zt-country-list">
              {filtered.length === 0 && <div className="zt-country-empty">No countries found</div>}
              {filtered.map((opt, i) => {
                const isSelected = (opt.value || undefined) === (value || undefined);
                return (
                  <button
                    type="button"
                    key={opt.value || "international"}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => pick(opt.value)}
                    className={`zt-country-option ${isSelected ? "is-selected" : ""} ${i === activeIndex ? "is-active" : ""}`}
                  >
                    <Icon aria-hidden country={opt.value} label={opt.label} />
                    <span className="zt-country-option-label">{opt.label}</span>
                    {isSelected && <Check aria-hidden size={14} className="zt-country-check" />}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
